import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '@/lib/utils';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
/**
 * Slide-in panel (shadcn Sheet, built on Radix Dialog). Used to house
 * sidebars and rails on narrow viewports where a persistent column would
 * steal too much width — the trigger lives in the surface's header.
 */
const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;
const SheetPortal = SheetPrimitive.Portal;
function SheetOverlay({ className, ref, ...props }) {
    return (_jsx(SheetPrimitive.Overlay, { className: cn('data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80', className), ...props, ref: ref }));
}
/* Each side pads for the screen edges it touches (index.html sets
   viewport-fit=cover, so in iPhone landscape a side sheet would otherwise sit
   under the sensor housing and home indicator; env() is 0 elsewhere). The base
   is p-0, so these only add the inset; a consumer passing its own p-* owns the
   insets too. */
const sheetVariants = cva('fixed z-50 gap-4 bg-background p-0 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out', {
    variants: {
        side: {
            top: 'inset-x-0 top-0 border-b pt-[env(safe-area-inset-top)] pr-[env(safe-area-inset-right)] pl-[env(safe-area-inset-left)] data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
            bottom: 'inset-x-0 bottom-0 border-t pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
            left: 'inset-y-0 left-0 h-full w-3/4 border-r pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
            right: 'inset-y-0 right-0 h-full w-3/4 border-l pt-[env(safe-area-inset-top)] pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
        },
    },
    defaultVariants: { side: 'left' },
});
/* The close X sits 1rem inside whichever edges it shares with the screen,
   plus that edge's inset. Its icon stays centred there; only the box grows:
   32px everywhere (the bare 16px icon was under the WCAG 24px minimum), 44px on
   touch-first devices. */
const sheetCloseVariants = cva('ring-offset-background focus:ring-ring absolute z-10 -m-2 inline-flex size-8 items-center justify-center rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none pointer-coarse:-m-3.5 pointer-coarse:size-11', {
    variants: {
        side: {
            top: 'top-[calc(1rem_+_env(safe-area-inset-top))] right-[calc(1rem_+_env(safe-area-inset-right))]',
            bottom: 'top-4 right-[calc(1rem_+_env(safe-area-inset-right))]',
            left: 'top-[calc(1rem_+_env(safe-area-inset-top))] right-4',
            right: 'top-[calc(1rem_+_env(safe-area-inset-top))] right-[calc(1rem_+_env(safe-area-inset-right))]',
        },
    },
    defaultVariants: { side: 'left' },
});
function SheetContent({ side = 'left', className, children, ref, ...props }) {
    const { t } = useTranslation();
    return (_jsxs(SheetPortal, { children: [_jsx(SheetOverlay, {}), _jsxs(SheetPrimitive.Content, { ref: ref, className: cn(sheetVariants({ side }), className), ...props, children: [children, _jsxs(SheetPrimitive.Close, { className: sheetCloseVariants({ side }), children: [_jsx(X, { className: "h-4 w-4" }), _jsx("span", { className: "sr-only", children: t('dialog.close') })] })] })] }));
}
function SheetTitle({ className, ref, ...props }) {
    return (_jsx(SheetPrimitive.Title, { ref: ref, className: cn('text-foreground text-lg font-semibold', className), ...props }));
}
function SheetDescription({ className, ref, ...props }) {
    return (_jsx(SheetPrimitive.Description, { ref: ref, className: cn('text-muted-foreground text-sm', className), ...props }));
}
export { Sheet, SheetClose, SheetContent, SheetDescription, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger, };
